<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="waitingForSeverResponse"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
		@keydown.enter="validateAndCommitCore"
		scrollable
	>
		<v-card
			class="modify-core-dialog"
			data-test="modify-core-dialog"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				{{ formTitle }}
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
					data-test="modify-core-dialog-close-button"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<v-form
					ref="addCoreForm"
					v-model="formValid"
					:disabled="!isEditable"
					class="stack"
				>
					<div
						v-if="modifyCoreRequest"
						class="id-row"
					>
						<div class="input-group id-row__field">
							<div class="field-label">Core ID</div>
							<v-text-field
								v-model="coreConfig.CoreID"
								required
								disabled
								hide-details="auto"
							/>
						</div>
						<CopyToClipboardButton
							:value-to-copy="coreConfig.CoreID"
							value-name="Core ID"
						/>
					</div>

					<div class="name-class-row">
						<div
							class="input-group name-class-row__name"
							data-test="core-name-input-container"
						>
							<div class="field-label">Core Name (required)</div>
							<v-text-field
								v-model="coreConfig.Name"
								:rules="[
									(v) => !!v || 'Core Name is required',
									(v) =>
										v.length <= maxCoreNameLength ||
										`Core Name has to be less than ${maxCoreNameLength} characters long.`,
									(v) =>
										!existingCoreNames.some((c) => {
											return c == v;
										}) || 'Core with this name already exists!',
								]"
								required
								:autofocus="true"
								data-test="core-name-input"
								hide-details="auto"
							/>
						</div>
						<div class="input-group name-class-row__class">
							<div class="field-label">Core Class</div>
							<v-select
								v-model="coreConfig.Class"
								:items="creatableCoreClasses"
								item-title="Name"
								item-value="Name"
								item-disabled="!Enabled"
								@update:model-value="coreClassChangeHandler"
								data-test="core-class-input"
								hide-details="auto"
							>
								<template v-slot:item="{ props }">
									<v-list-item v-bind="props" />
								</template>
							</v-select>
						</div>
					</div>
					<v-expand-transition>
						<v-alert
							v-show="!availableSelectedCoreClass"
							:icon="false"
							border="start"
							class="sgnl-paragraph-s-regular"
							type="warning"
							variant="outlined"
							elevation="0"
						>
							<div class="text-block">
								Selected Core Class is not available on {{ currentUserTier }} tier.
							</div>
							<UpgradeButton />
						</v-alert>
					</v-expand-transition>
					<div
						class="input-group"
						data-test="core-microarchitecture-input-container"
					>
						<div class="field-label">Microachitecture</div>
						<v-select
							hide-details
							:menu-props="{ class: 'core-select-menu' }"
							v-model="coreConfig.Microarchitecture"
							:items="microarchitecturesSupportedByCoreClass(coreConfig.Class)"
							item-title="Name"
							item-value="Name"
							item-disabled="Disabled"
							@update:model-value="microarchitectureChangeHandler"
							data-test="core-microarchitecture-input"
						>
							<template v-slot:item="{ item, props }">
								<v-list-item
									v-bind="props"
									:data-test="`core-microarchitecture-${item.raw.Name}`"
								>
									<template #append>
										<v-chip
											v-if="item.raw.Disabled && !item.raw.Restricted"
											size="x-small"
											color="secondary"
											variant="outlined"
										>
											Unavailable
										</v-chip>
										<v-chip
											v-if="item.raw.Restricted"
											size="x-small"
											color="secondary"
											variant="outlined"
										>
											Tier Restricted
										</v-chip>
										<v-chip
											v-if="item.raw.Experimental"
											size="x-small"
											class="badge-brand"
										>
											Alpha release
										</v-chip>
									</template>
								</v-list-item>
							</template>
						</v-select>
					</div>
					<v-expand-transition>
						<v-alert
							v-show="!availableSelectedMicroarchitecture"
							:icon="false"
							border="start"
							class="sgnl-paragraph-s-regular"
							type="warning"
							variant="outlined"
							elevation="0"
						>
							<div class="text-block">
								Selected Microarchitecture not available on {{ currentUserTier }} tier.
							</div>
							<UpgradeButton />
						</v-alert>
					</v-expand-transition>
					<template v-if="coreSupportsCustomPrecision && coreSupportsSigmaEquivalent">
						<div
							class="input-group"
							data-test="core-mcequivalent-input-container"
						>
							<div class="field-label sgnl-flex sgnl-align-center sgnl-gap-8">
								Monte Carlo Equivalent
								<DisclosureIcon
									disclosure-text="For a given core representation size, the sigma (σ) value corresponds to the number of standard deviations of a Gaussian distribution that the Signaloid core can accurately represent. A sigma value of x means that the Signaloid core can accurately represent a Gaussian distribution within the domain [μ - xσ, μ + xσ], where μ is the mean and σ is the standard deviation of the Gaussian distribution. The higher the value, the more accurate the Signaloid core is at capturing the tails of distributions."
								/>
							</div>
							<v-slider
								v-model="selectedPrecisionIndex"
								:ticks="sigmaStepsLabelWithFilter"
								hide-details
								class="sgnl-caption-regular"
								:max="sigmaSteps.length - 1"
								step="1"
								:thumb-size="24"
								thumb-label
								@update:model-value="corePrecisionChangeHandler"
								data-test="core-mcequivalent-input"
							>
								<template v-slot:thumb-label="{ modelValue }">
									{{ sigmaStepsLabel[modelValue] }}
								</template>
							</v-slider>
						</div>
					</template>
					<template v-if="coreSupportsCustomPrecision">
						<div
							class="input-group"
							data-test="core-precision-input-container"
						>
							<div class="field-label">Signaloid Representation Size</div>
							<v-slider
								v-model="selectedPrecisionIndex"
								:ticks="filteredPrecisionSteps"
								hide-details
								class="sgnl-caption-regular"
								:max="precisionSteps.length - 1"
								step="1"
								:thumb-size="24"
								thumb-label
								@update:model-value="corePrecisionChangeHandler"
								data-test="core-precision-input"
							>
								<template v-slot:thumb-label="{ modelValue }">
									{{ precisionSteps[modelValue] }}
								</template>
							</v-slider>
						</div>
						<v-expand-transition>
							<v-alert
								v-show="!availablePrecisionStep"
								:icon="false"
								border="start"
								class="sgnl-paragraph-s-regular"
								type="warning"
								variant="outlined"
								elevation="0"
							>
								<div class="text-block">
									Selected Representation Size not available on {{ currentUserTier }} tier.
								</div>
								<UpgradeButton />
							</v-alert>
						</v-expand-transition>
					</template>
					<template v-if="coreConfig.Microarchitecture == 'Reference'">
						<div
							class="input-group"
							data-test="core-precision-input-container"
						>
							<div class="field-label">Reference Quality Level</div>
							<v-slider
								v-model="selectedReferencePrecisionIndex"
								:ticks="filteredReferencePrecisionSteps"
								hide-details
								class="sgnl-caption-regular"
								:max="referencePrecisionSteps.length - 1"
								step="1"
								:thumb-size="24"
								thumb-label
								@update:model-value="corePrecisionChangeHandler"
								data-test="core-precision-input"
							>
								<template v-slot:thumb-label="{ modelValue }">
									{{ referencePrecisionStepsLabel[modelValue] }}
								</template>
							</v-slider>
						</div>
						<v-expand-transition>
							<v-alert
								v-show="!availablePrecisionStep"
								:icon="false"
								border="start"
								class="sgnl-paragraph-s-regular"
								type="warning"
								variant="outlined"
								elevation="0"
							>
								<div class="text-block">
									Selected Reference Quality Level not available on
									{{ currentUserTier }} tier.
								</div>
								<UpgradeButton />
							</v-alert>
						</v-expand-transition>
					</template>

					<div
						class="input-group"
						data-test="core-memory-input-container"
					>
						<div class="field-label">Memory Allocation</div>
						<v-slider
							v-model="selectedMemorySizeIndex"
							:ticks="filteredMemorySizeSteps"
							hide-details
							class="sgnl-caption-regular"
							:max="memorySizeSteps.length - 1"
							step="1"
							:thumb-size="24"
							thumb-label
							@update:model-value="updateCoreMemorySize"
							data-test="core-memory-input"
						>
							<template v-slot:thumb-label="{ modelValue }">
								{{ memorySizeStepsLabel[modelValue] }}
							</template>
						</v-slider>
					</div>
					<v-expand-transition>
						<v-alert
							v-show="!availableMemorySizeStep"
							:icon="false"
							border="start"
							class="sgnl-paragraph-s-regular"
							type="warning"
							variant="outlined"
							elevation="0"
						>
							<div class="text-block">
								Selected Memory Allocation not available on
								{{ currentUserTier }} tier.
							</div>
							<UpgradeButton />
						</v-alert>
					</v-expand-transition>
					<div
						v-if="coreConfig.Microarchitecture !== 'Reference'"
						class="input-group"
						data-test="core-correlation-input-container"
					>
						<div class="field-label">Correlation Tracking</div>
						<v-select
							hide-details="auto"
							v-if="supportedCorrelationTrackingMethods(coreConfig.Microarchitecture).length > 0"
							v-model="coreConfig.CorrelationTracking"
							:items="supportedCorrelationTrackingMethods(coreConfig.Microarchitecture)"
							item-title="Name"
							item-value="Code"
							data-test="core-correlation-input"
						>
							<template v-slot:item="{ item, props }">
								<v-list-item v-bind="props">
									<template
										#append
										v-if="item.raw.Experimental"
									>
										<v-chip
											size="x-small"
											class="badge-brand"
										>
											Alpha release
										</v-chip>
									</template>
								</v-list-item>
							</template>
						</v-select>
					</div>
				</v-form>
				<div
					v-if="coreDescription"
					class="sgnl-paragraph-s-regular"
				>
					{{ coreDescription }}
				</div>
				<v-alert
					v-if="remoteErrorMessage"
					:icon="false"
					border="start"
					class="sgnl-paragraph-s-regular"
					type="warning"
					variant="outlined"
					elevation="0"
				>
					{{ remoteErrorMessage }}
				</v-alert>
				<v-alert
					v-if="!modifyCoreRequest && customCoresLimitReached"
					:icon="false"
					border="start"
					class="sgnl-paragraph-s-regular"
					type="warning"
					variant="outlined"
					elevation="0"
				>
					<div class="text-block">Custom Core Limit Reached</div>
					<UpgradeButton />
				</v-alert>
			</v-card-text>
			<v-divider />
			<v-card-actions
				v-if="isEditable"
				class="dialog-footer"
			>
				<v-btn
					variant="outlined"
					@click="handleDismiss"
				>
					Cancel
				</v-btn>
				<LimitableActionButton
					@valid-click="validateAndCommitCore"
					:loading="waitingForSeverResponse"
					variant="flat"
					color="primary"
					width="auto"
					data-test="commit-core-button"
					:disabled="submitButtonDisabled"
					:limits-to-check="[UserLimitsE.AllowedCoreClasses]"
					:resource-usage-overrides="[coreConfig.Class]"
				>
					<template #btnContent>
						{{ submitButtonText }}
					</template>
				</LimitableActionButton>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
// Stores
import { mapState, mapStores } from "pinia";
import { useCoresStore } from "@/stores/cores";
import { useUserStore } from "@/stores/user";

// utilities
import { sdk } from "@/js/sdk";
import {
	SdkError,
	ERROR_CODES,
	type CoreClass,
	type Microarchitecture,
	type CorrelationTracking,
} from "@signaloid/cloud-compute-engine-client";
import {
	filterEveryNValue,
	symmetricFilter,
	toStringWithSIMagnitude,
	toStringWithByteMagnitude,
	coreDescriptionString,
} from "@/js/utilities";

import { monitoringCaptureError } from "@/plugins/monitoring";

// components
import UpgradeButton from "@/components/Common/UpgradeButton.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import DisclosureIcon from "@/components/Common/DisclosureIcon.vue";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import Close16 from "@carbon/icons-vue/es/close/16";

// libraries
import axios from "axios";

// types
import { CoreClassObject, CoreMicroarchitectureObject, CreateCoreConfig } from "@/types/api/cores";
import { UserLimitsE, withinLimit } from "@/js/tierconfig";

type PartialCoreToDisplay = CreateCoreConfig & { CoreID; Owner };

const curatedMemorySizeSteps = [256e3, 512e3, 1e6, 2e6, 4e6, 16e6, 32e6, 64e6, 128e6, 256e6, 512e6, 1e9, 2e9];

const defaultCoreData: PartialCoreToDisplay = {
	CoreID: undefined,
	Owner: "",
	Name: "",
	Class: "C0Pro",
	Precision: 32,
	MemorySize: 2_000_000,
	Microarchitecture: "Athens",
	CorrelationTracking: "Disable",
};

type ComponentData = {
	coreConfig: PartialCoreToDisplay;
	formValid: boolean;
	waitingForSeverResponse: boolean;
	remoteErrorMessage: undefined | string;
	precisionSteps: number[];
	sigmaSteps: number[];
	referencePrecisionSteps: number[];
	selectedPrecisionIndex: number;
	selectedMemorySizeIndex: number;
	selectedReferencePrecisionIndex: number;
	existingCoreNames: string[];
	formTitle: string;
	submitButtonText: string;
	maxCoreNameLength: number;
};

export default defineComponent({
	name: "ModifyCoreDialog",
	components: { UpgradeButton, LimitableActionButton, DisclosureIcon, CopyToClipboardButton, Close16 },
	emits: ["core-updated", "core-created", "close-dialog", "update:showDialog"],
	props: { showDialog: Boolean, editCoreData: { type: Object as PropType<PartialCoreToDisplay> } },
	data: (): ComponentData => ({
		coreConfig: { ...defaultCoreData },
		formValid: false,
		waitingForSeverResponse: false,
		remoteErrorMessage: undefined,
		selectedPrecisionIndex: 3,
		selectedMemorySizeIndex: 0,
		selectedReferencePrecisionIndex: 0,
		precisionSteps: [4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, 4096, 8192],
		sigmaSteps: [1.37, 1.83, 2.22, 2.57, 2.89, 3.18, 3.45, 3.7, 3.94, 4.17, 4.39, 4.6],
		referencePrecisionSteps: [
			4, 8, 16, 32, 64, 128, 256, 512, 1e3, 2e3, 4e3, 8e3, 16e3, 32e3, 64e3, 128e3, 256e3, 512e3, 1e6,
		],
		existingCoreNames: [],
		formTitle: "Add Execution Core",
		submitButtonText: "Create Core",
		maxCoreNameLength: 64,
	}),
	setup() {
		return { UserLimitsE };
	},
	methods: {
		microarchitectureChangeHandler() {
			// update core precision
			this.corePrecisionChangeHandler();

			// Reset Autocorrelation tracking on microarchitecture change
			this.coreConfig.CorrelationTracking = this.supportedCorrelationTrackingMethods(
				this.coreConfig.Microarchitecture,
			)[0].Code;
		},
		coreClassChangeHandler() {
			// update core precision
			this.corePrecisionChangeHandler();

			// Reset core microarchitecture on core class change
			this.coreConfig.Microarchitecture = this.microarchitecturesSupportedByCoreClass(
				this.coreConfig.Class,
			)[0].Name;

			// Reset correlation tracking on core class change
			this.coreConfig.CorrelationTracking = this.supportedCorrelationTrackingMethods(
				this.coreConfig.Microarchitecture,
			)[0].Code;
		},
		corePrecisionChangeHandler() {
			this.coreConfig.Precision =
				this.coreConfig.Microarchitecture === "Reference"
					? this.referencePrecisionSteps[this.selectedReferencePrecisionIndex]
					: this.precisionSteps[this.selectedPrecisionIndex];
		},
		updateCoreMemorySize() {
			this.coreConfig.MemorySize = this.memorySizeSteps[this.selectedMemorySizeIndex];
		},
		async validateAndCommitCore() {
			this.remoteErrorMessage = undefined;
			// @ts-ignore
			this.$refs.addCoreForm.validate();
			if (this.formValid) {
				try {
					this.waitingForSeverResponse = true;
					const coreRequestPayload = {
						Name: this.coreConfig.Name,
						Class: this.coreConfig.Class as CoreClass,
						Precision: this.coreConfig.Precision!,
						MemorySize: this.coreConfig.MemorySize,
						Microarchitecture: this.coreConfig.Microarchitecture as Microarchitecture,
						CorrelationTracking: this.coreConfig.CorrelationTracking as CorrelationTracking,
					};
					if (this.modifyCoreRequest && !this.isLocalCore) {
						await sdk.cores.update(this.coreConfig.CoreID, coreRequestPayload);
						this.$emit("core-updated", this.coreConfig);
						this.$posthog?.capture("custom_core_edited", { core_id: this.coreConfig?.CoreID });
					} else {
						await sdk.cores.create(coreRequestPayload);

						this.$emit("core-created", this.coreConfig);
						this.$posthog?.capture("custom_core_created", {
							microarchitecture: this.coreConfig?.Microarchitecture,
						});

						if (this.isLocalCore) {
							// @ts-ignore deprecated code path. local cores are no longer used
							this.coresStore.removeCoreFromLocalCoresList(this.coreConfig);
						}
					}
					// @ts-ignore
					this.$refs.addCoreForm.resetValidation();
					this.resetCoreData();
					this.closeDialog();
				} catch (error) {
					monitoringCaptureError(error, "Save core config");
					if (error instanceof SdkError) {
						this.remoteErrorMessage =
							error.code === ERROR_CODES.API_FORBIDDEN
								? "Could not save core due to tier restrictions."
								: error.message || "An error occurred while saving the core.";
					} else if (axios.isAxiosError(error)) {
						this.remoteErrorMessage =
							"Failed to save the core. Make sure you have an active internet connection.";
					} else {
						this.remoteErrorMessage = "An error occurred while saving the core.";
					}
				} finally {
					this.waitingForSeverResponse = false;
				}
			}
		},
		handleDismiss() {
			if (this.waitingForSeverResponse) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			// @ts-ignore
			this.$refs.addCoreForm.resetValidation();
			this.resetCoreData();
			this.$emit("close-dialog");
		},
		resetCoreData() {
			this.coreConfig = { ...defaultCoreData };
		},
		parseSliderValuesFromCoreConfig() {
			// calculate the slider indices from core config
			this.selectedMemorySizeIndex = Math.max(
				0,
				this.memorySizeSteps.findIndex((x) => x === this.coreConfig.MemorySize),
			);
			if (this.coreConfig.Microarchitecture === "Reference") {
				this.selectedReferencePrecisionIndex = Math.max(
					0,
					this.referencePrecisionSteps.findIndex((x) => x === this.coreConfig.Precision),
				);
			} else {
				// FIXME: this should show between steps this will cause an issue to unknown values
				this.selectedPrecisionIndex = Math.max(
					0,
					this.precisionSteps.findIndex((x) => x === this.coreConfig.Precision),
				);
			}
		},
		supportedCorrelationTrackingMethods(microachitecture) {
			return this.coresStore.correlationTrackingSupportedByCore(microachitecture);
		},
		microarchitecturesSupportedByCoreClass(coreClass) {
			return this.coresStore.microarchitecturesSupportedByCoreClass(coreClass);
		},
	},
	computed: {
		...mapStores(useCoresStore, useUserStore),
		...mapState(useCoresStore, { filteredCoresList: "filteredCoresList" }),
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		memorySizeSteps(): number[] {
			const servedMemorySizeRange = this.userStore.currentUserObjectFromDB?.FieldConstraints?.MemorySize;
			if (!servedMemorySizeRange) {
				return curatedMemorySizeSteps;
			}
			const stepsWithinServedRange = curatedMemorySizeSteps.filter(
				(step) => step >= servedMemorySizeRange.Minimum && step <= servedMemorySizeRange.Maximum,
			);
			return stepsWithinServedRange.at(-1) === servedMemorySizeRange.Maximum
				? stepsWithinServedRange
				: [...stepsWithinServedRange, servedMemorySizeRange.Maximum];
		},
		memorySizeStepsLabel(): string[] {
			return this.memorySizeSteps.map((v) => toStringWithByteMagnitude(v, ""));
		},
		currentUserTier(): string | undefined {
			return this.userStore?.currentUserTierDetails?.title;
		},
		availablePrecisionStep(): boolean {
			const thisUserRestrictions = this.userStore.currentUserTierDetails;
			if (!thisUserRestrictions) {
				return false;
			}
			const isReference = this.coreConfig.Microarchitecture === "Reference";
			const steps = isReference ? this.referencePrecisionSteps : this.precisionSteps;
			const maxPrecision = isReference
				? thisUserRestrictions.allowance.MaxReferenceQuality
				: thisUserRestrictions.allowance.MaxPrecisionBits;
			const filteredByTierPrecisionSteps = steps.filter((ps) => withinLimit(ps, maxPrecision));

			const selectedIndex =
				this.coreConfig.Microarchitecture === "Reference"
					? this.selectedReferencePrecisionIndex
					: this.selectedPrecisionIndex;
			return filteredByTierPrecisionSteps.includes(steps[selectedIndex]);
		},
		availableMemorySizeStep(): boolean {
			const currentUserTier = this.userStore.currentUserTierDetails;
			if (!currentUserTier) {
				return false;
			}
			const filteredByTierMemorySteps = this.memorySizeSteps.filter((ms) =>
				withinLimit(ms, currentUserTier.allowance.MaximumAttachedProcessorRam),
			);
			return filteredByTierMemorySteps.includes(this.memorySizeSteps[this.selectedMemorySizeIndex]);
		},
		availableMicroarchitectures(): CoreMicroarchitectureObject[] {
			const currentUserTier = this.userStore.currentUserTierDetails;
			if (!currentUserTier) {
				return [];
			}
			const disabledByTierAvailableMicroarchitectures = this.coresStore.availableMicroarchitectures.map((am) => {
				const isAllowed = currentUserTier.allowance.AllowedMicroArchitectures.includes(am.Name);
				// Will set property if not there already
				am.Restricted = !isAllowed;
				return am;
			});
			return disabledByTierAvailableMicroarchitectures;
		},
		availableSelectedMicroarchitecture(): boolean {
			const selectedMicroarchitecture = this.coreConfig.Microarchitecture;
			const filteredArch = this.availableMicroarchitectures.filter((am) => am.Name == selectedMicroarchitecture);
			return filteredArch.length > 0 && !filteredArch[0].Restricted;
		},
		availableCoreClasses(): CoreClassObject[] {
			const currentUserTier = this.userStore.currentUserTierDetails;
			if (!currentUserTier) {
				return [];
			}
			const disabledByTierAvailableCoreClasses = this.coresStore.availableCoreClasses.map((coreClass) => {
				const isAllowed = currentUserTier.allowance.AllowedCoreClasses.includes(coreClass.Name);
				return { ...coreClass, Enabled: isAllowed };
			});
			return disabledByTierAvailableCoreClasses;
		},
		creatableCoreClasses(): CoreClassObject[] {
			return this.availableCoreClasses.filter((coreClass) => coreClass.Creatable);
		},
		availableSelectedCoreClass(): boolean {
			const selectedCoreClass = this.coreConfig.Class;
			const filteredArch = this.availableCoreClasses.filter((coreClass) => coreClass.Name == selectedCoreClass);

			return filteredArch.length > 0 && filteredArch[0].Enabled;
		},
		customCoresLimitReached(): boolean {
			const currentDefaultCoresCount = this.coresStore.getCustomCores.length;
			const currentUserTier = this.userStore.currentUserTierDetails;
			// If current user tier details are not available, be less restrictive
			if (!currentUserTier) {
				return false;
			}

			const customCoresCountAfterCreation = currentDefaultCoresCount + 1;
			return !withinLimit(customCoresCountAfterCreation, currentUserTier.allowance.CoreCount);
		},
		submitButtonDisabled(): boolean {
			// If we are on Create Popup and we reached custom core limit count
			const customCoreLimitReachedOnCreation = !this.modifyCoreRequest && this.customCoresLimitReached;
			// Precision is out of tier limit
			const precisionNotAvailable = !this.availablePrecisionStep;
			// Memory is out of tier limit
			const memoryNotAvailable = !this.availableMemorySizeStep;
			// Architecture is out of tier limit
			const microarchitectureNotAvailable = !this.availableSelectedMicroarchitecture;
			// Core class is out of tier limit
			const coreClassNotAvailable = !this.availableSelectedCoreClass;

			return (
				coreClassNotAvailable ||
				customCoreLimitReachedOnCreation ||
				precisionNotAvailable ||
				memoryNotAvailable ||
				microarchitectureNotAvailable
			);
		},
		precisionStepsLabel(): string[] {
			return this.precisionSteps.map((v) => toStringWithSIMagnitude(v, ""));
		},
		sigmaStepsLabel(): string[] {
			const sigma = String.fromCodePoint(963);
			return this.sigmaSteps.map((v) => {
				return v + sigma;
			});
		},
		sigmaStepsLabelWithFilter(): Record<number, string> {
			const sigma = String.fromCodePoint(963);
			const valuesWithSigmaSymbol = this.sigmaSteps.map((v) => {
				return v + sigma;
			});
			const filtered = symmetricFilter(valuesWithSigmaSymbol, 1);
			const result: Record<number, string> = {};
			for (let i = 0; i < filtered.length; i++) {
				result[i] = filtered[i] ?? "";
			}
			return result;
		},
		referencePrecisionStepsLabel(): string[] {
			return this.referencePrecisionSteps.map((v) => toStringWithSIMagnitude(v, ""));
		},
		filteredMemorySizeSteps(): Record<number, string> {
			const filtered = this.$vuetify.display.mobile
				? filterEveryNValue(this.memorySizeStepsLabel, 3)
				: symmetricFilter(this.memorySizeStepsLabel, 1);
			const result: Record<number, string> = {};
			for (let i = 0; i < filtered.length; i++) {
				result[i] = filtered[i] ?? "";
			}
			return result;
		},
		filteredPrecisionSteps(): Record<number, string> {
			const filtered = this.$vuetify.display.mobile
				? filterEveryNValue(this.precisionStepsLabel, 2)
				: this.precisionStepsLabel;
			const result: Record<number, string> = {};
			for (let i = 0; i < filtered.length; i++) {
				result[i] = filtered[i] ?? "";
			}
			return result;
		},
		filteredReferencePrecisionSteps(): Record<number, string> {
			const filtered = filterEveryNValue(this.referencePrecisionStepsLabel, 3);
			const result: Record<number, string> = {};
			for (let i = 0; i < filtered.length; i++) {
				result[i] = filtered[i] ?? "";
			}
			return result;
		},
		coreSupportsCustomPrecision(): boolean {
			return this.coresStore.customPrecisionSupportedByCore(this.coreConfig.Microarchitecture);
		},
		coreSupportsSigmaEquivalent(): boolean {
			return this.coreConfig.Microarchitecture == "Athens";
		},
		modifyCoreRequest(): boolean {
			return this.editCoreData !== undefined;
		},
		isLocalCore(): boolean {
			return this.coreConfig.CoreID && !this.coreConfig.CoreID.includes("cor_");
		},
		isEditable(): boolean {
			if (this.coreConfig && this.coreConfig.Owner !== "*") {
				return true;
			}
			return false;
		},
		coreDescription() {
			if (this.coreConfig && this.coreConfig.Owner == "*") {
				const description = coreDescriptionString(this.coreConfig.Name, false);
				return description;
			} else {
				return "";
			}
		},
	},
	created() {
		this.coresStore.applyAccountCoreCapabilities();
		this.userStore.getCurrentUserTierDetails().then(() => this.coresStore.applyAccountCoreCapabilities());
		this.existingCoreNames = this.filteredCoresList.map((v) => v.Name);

		if (this.editCoreData !== undefined) {
			this.coreConfig = { ...this.editCoreData };
			this.existingCoreNames = this.existingCoreNames.filter((v) => v != this.coreConfig.Name);

			// reset correlation if invalid
			if (
				typeof this.coreConfig.CorrelationTracking === "object" &&
				this.coreConfig.CorrelationTracking !== null &&
				!Array.isArray(this.coreConfig.CorrelationTracking)
			) {
				console.warn("correlation tracking is set as an object");
				this.coreConfig.CorrelationTracking =
					// @ts-ignore deprecated code path
					this.coreConfig.CorrelationTracking?.Code ?? defaultCoreData.CorrelationTracking;
			}
		}

		this.parseSliderValuesFromCoreConfig();

		this.formTitle = !this.isEditable
			? "Execution Core Details"
			: this.modifyCoreRequest
				? "Modify Execution Core"
				: "Add Execution Core";
		this.submitButtonText = this.modifyCoreRequest ? "Save Core" : "Create Core";
	},
});
</script>

<style lang="scss">
.modify-core-dialog .id-row {
	display: flex;
	align-items: flex-end;
	gap: var(--sgnl-space-8);
}

.modify-core-dialog .id-row__field {
	flex: 1;
}

.modify-core-dialog .name-class-row {
	display: flex;
	align-items: flex-start;
	gap: var(--sgnl-space-16);
}

.modify-core-dialog .name-class-row__name {
	flex: 2;
}

.modify-core-dialog .name-class-row__class {
	flex: 1;
}

.modify-core-dialog .v-alert__content {
	overflow: visible;
}

/* #2 slider runs full width (Vuetify insets it on .v-slider.v-input--horizontal) */
.modify-core-dialog .v-slider.v-input--horizontal {
	margin-inline: 0;
}

.modify-core-dialog .v-slider--has-labels > .v-input__control {
	margin-bottom: 0;
	padding-bottom: var(--sgnl-space-12);
}

/* tick labels sit a consistent distance below the track */
.modify-core-dialog .v-slider.v-input--horizontal .v-slider-track__tick .v-slider-track__tick-label {
	margin-top: var(--sgnl-space-12);
}

/* #1 disabled Core ID field matches the others' 36px height */
.modify-core-dialog .id-row__field .v-field,
.modify-core-dialog .id-row__field .v-field__input {
	min-height: 36px;
}

.modify-core-dialog .id-row__field .v-field__input {
	padding-top: 0;
	padding-bottom: 0;
}

/* dropdown menu rows: drop the empty prepend spacer */
.core-select-menu .v-list-item__spacer {
	width: 0;
}

@media (max-width: 599px) {
	.modify-core-dialog .v-card-actions {
		flex-direction: column-reverse;
	}

	.modify-core-dialog .v-card-actions .v-btn {
		width: 100%;
	}

	.modify-core-dialog .v-card-actions > * {
		width: 100%;
	}
}
</style>
